/* ─────────────────────────────────────────────────────────────────
   BACKEND NODE.JS DEVELOPMENT — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx
───────────────────────────────────────────────────────────────── */

export const BACKEND_NODE_MODULES = [
  {
    module: 1,
    title: "Node.js Fundamentals",
    emoji: "🟢",
    topics: [
      {
        id: 1,
        topic: "Node.js & npm Basics",
        description: "Learn how Node.js runs JavaScript outside the browser, and how to manage packages with npm.",
        motivation: "Great start! Node.js is what lets JavaScript power servers — you just took your first backend step. 🟢",
        resource: {
          name: "Node.js Official Docs — Introduction",
          url: "https://nodejs.org/en/learn/getting-started/introduction-to-nodejs",
        },
        task: "Build a command-line File Organizer script using Node.js.",
        requirements: [
          "Read all files in a given folder using the fs module",
          "Sort files into subfolders by extension (images, docs, others)",
          "Log a summary of how many files were moved",
          "Use npm to add and use at least one utility package (e.g. chalk for colored output)",
          "Include a package.json with a start script",
        ],
      },
      {
        id: 2,
        topic: "Building a Server with Express.js",
        description: "Learn to build a web server and define routes using the Express.js framework.",
        motivation: "Your first server is live! Express is the exact framework powering countless production APIs. 🚀",
        resource: {
          name: "Express.js Official Guide — Routing",
          url: "https://expressjs.com/en/guide/routing.html",
        },
        task: "Build a REST API for a simple blog application with full CRUD operations.",
        requirements: [
          "Endpoints: GET /posts, POST /posts, PUT /posts/:id, DELETE /posts/:id",
          "Use an in-memory array as the data store for now",
          "Return proper HTTP status codes (200, 201, 404, 400)",
          "Add input validation for POST/PUT requests",
          "Test all endpoints using Postman or Thunder Client",
        ],
      },
    ],
  },
  {
    module: 2,
    title: "REST APIs & Databases",
    emoji: "🗄️",
    topics: [
      {
        id: 3,
        topic: "RESTful API Design & CRUD Operations",
        description: "Learn REST conventions and how to structure a clean, predictable API.",
        motivation: "Your API follows real REST conventions now — this is what makes an API easy for any frontend to consume. 📐",
        resource: {
          name: "MDN — An Overview of HTTP",
          url: "https://developer.mozilla.org/en-US/docs/Web/HTTP/Overview",
        },
        task: "Extend your Blog API into a Task Manager API with nested resources.",
        requirements: [
          "Endpoints: GET /tasks, POST /tasks, PUT /tasks/:id, DELETE /tasks/:id",
          "Add a nested resource: POST /tasks/:id/comments",
          "Support filtering via query params (e.g. GET /tasks?completed=true)",
          "Use consistent JSON response shapes across all endpoints",
          "Document all endpoints in a README.md",
        ],
      },
      {
        id: 4,
        topic: "MongoDB & Mongoose Integration",
        description: "Learn to persist data to a real database using MongoDB and Mongoose.",
        motivation: "Data persists now — your API has real memory. That's production-level thinking! 🗄️",
        resource: {
          name: "Mongoose Official Documentation",
          url: "https://mongoosejs.com/docs/guide.html",
        },
        task: "Add MongoDB persistence to your Task Manager API using Mongoose.",
        requirements: [
          "Define a Task schema: title, description, completed, createdAt",
          "Connect Express to a free MongoDB Atlas cluster",
          "All CRUD operations must persist to the database",
          "Implement pagination for GET /tasks",
          "Handle database connection and query errors gracefully",
        ],
      },
    ],
  },
  {
    module: 3,
    title: "Authentication & Security",
    emoji: "🔐",
    topics: [
      {
        id: 5,
        topic: "JWT Authentication",
        description: "Learn how to secure an API with user registration, login, and JWT tokens.",
        motivation: "Security unlocked! You now understand how auth works under the hood of every major app. 🔐",
        resource: {
          name: "JWT Introduction — jwt.io",
          url: "https://jwt.io/introduction",
        },
        task: "Add user registration and login to your Task Manager API using JWT.",
        requirements: [
          "POST /auth/register — hash passwords with bcrypt",
          "POST /auth/login — return a JWT token",
          "Protect POST, PUT, DELETE routes with auth middleware",
          "Return 401 for invalid or missing tokens",
          "Associate tasks with the logged-in user",
        ],
      },
      {
        id: 6,
        topic: "Middleware, Error Handling & Validation",
        description: "Learn to write custom middleware and centralize error handling and input validation.",
        motivation: "Your API fails gracefully now instead of crashing — that's what separates hobby code from real software. 🛡️",
        resource: {
          name: "Express.js Official Guide — Error Handling",
          url: "https://expressjs.com/en/guide/error-handling.html",
        },
        task: "Add centralized error handling and request validation to your API.",
        requirements: [
          "Write a custom error-handling middleware used across all routes",
          "Add a request logging middleware (method, path, response time)",
          "Validate request bodies using a library like Joi or express-validator",
          "Return consistent, descriptive error responses (no raw stack traces)",
          "Handle 404s for unknown routes cleanly",
        ],
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 7,
        topic: "Full Backend API — Task Manager with Auth",
        description: "Apply everything learned throughout the MicroIntern to ship a complete, secured backend API.",
        motivation: "Outstanding! You built a secure, persistent, well-structured backend from scratch. That's certificate-worthy. 🎉",
        resource: {
          name: "Express.js Official Guides",
          url: "https://expressjs.com/en/guide/routing.html",
        },
        task: "Polish your Task Manager API into a complete, production-style backend.",
        requirements: [
          "User registration, login, and JWT-protected routes",
          "Full CRUD for tasks, scoped to the logged-in user",
          "Centralized error handling and input validation throughout",
          "Environment variables used for all secrets (never hardcoded)",
          "Pagination and filtering on list endpoints",
          "Clean folder structure (routes/controllers/models separated)",
          "A well-documented README.md with setup and API reference",
        ],
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const BACKEND_NODE_TASKS = BACKEND_NODE_MODULES.flatMap((m) => m.topics);
